import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Button } from '@shared/ui/primitives/Button';
import { pollCheckoutStatus } from './api';

/**
 * Generic post-payment landing. The provider redirects the browser back
 * to one of:
 *   - `/checkout/payment/redirect`     (in-app or web fallback)
 *   - `/checkout/paynow/callback`      (Paynow-specific)
 *
 * Each callback identifies a session/order via query strings; we poll
 * status until paid/failed, then route to /checkout/success or back to
 * the cart with a toast.
 */
export function PaymentRedirect(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<'pending' | 'paid' | 'failed' | 'cancelled'>('pending');
  const [orderId, setOrderId] = useState<string | undefined>();

  const sessionId = params.get('session') ?? params.get('checkout_id') ?? '';

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    const tick = async () => {
      try {
        const result = await pollCheckoutStatus(sessionId);
        if (cancelled) return;
        setStatus(result.status);
        if (result.order_id) setOrderId(result.order_id);
        if (result.status === 'pending') {
          setTimeout(tick, 1500);
        } else if (result.status === 'paid' && result.order_id) {
          navigate(`/checkout/success?order=${result.order_id}`, { replace: true });
        }
      } catch (e) {
        console.warn('[checkout] poll failed:', e);
        if (!cancelled) setStatus('failed');
      }
    };
    void tick();

    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <Page scroll={false}>
      <div className="flex h-full flex-col items-center justify-center text-center gap-4">
        {status === 'pending' ? (
          <>
            <Loader2 className="h-10 w-10 text-primary-600 animate-spin" strokeWidth={1.5} />
            <h1 className="font-display text-display-xl text-ink">Confirming your payment…</h1>
            <p className="text-body text-ink-secondary">Don't close the app — this only takes a moment.</p>
          </>
        ) : null}
        {status === 'paid' ? (
          <>
            <CheckCircle2 className="h-10 w-10 text-success" strokeWidth={1.5} />
            <h1 className="font-display text-display-xl text-ink">Paid.</h1>
            <p className="text-body text-ink-secondary">Routing to your order.</p>
          </>
        ) : null}
        {status === 'failed' || status === 'cancelled' ? (
          <>
            <AlertCircle className="h-10 w-10 text-danger" strokeWidth={1.5} />
            <h1 className="font-display text-display-xl text-ink">
              {status === 'cancelled' ? 'Payment cancelled.' : "Payment didn't go through."}
            </h1>
            <p className="text-body text-ink-secondary">Try again — your cart is still here.</p>
            <Button onClick={() => navigate('/app/cart', { replace: true })}>Back to cart</Button>
          </>
        ) : null}
      </div>
      {orderId && status === 'paid' ? null : null}
    </Page>
  );
}

export function CheckoutSuccess(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get('order');

  return (
    <Page scroll={false}>
      <div className="flex h-full flex-col items-center justify-center text-center gap-4">
        <CheckCircle2 className="h-12 w-12 text-success" strokeWidth={1.5} />
        <h1 className="font-display text-display-2xl text-ink">Thank you.</h1>
        <p className="text-body text-ink-secondary max-w-sm">
          Your order is on its way. We've emailed your receipt.
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/app/orders', { replace: true })}>View orders</Button>
          <Button onClick={() => navigate('/app/home', { replace: true })}>Done</Button>
        </div>
        {orderId ? <p className="text-caption text-ink-muted mt-4">Order #{orderId}</p> : null}
      </div>
    </Page>
  );
}
